'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getTransaction } from 'data/api/get-transaction'
import {
  translateConversation,
  getConversation,
  Conversation,
  transformConversationResponse,
  TransactionModel,
  transformTransaction,
  getConversationNew,
  transformConversationNewResponse,
  ConversationMessage,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'

import { HttpStatus } from '@marketplace-web/core-api/api-client-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import {
  SendMessageUiState,
  GetConversationRequestArgs,
  TranslateConversationArgs,
  GetConversationUiState,
  GetTransactionUiState,
} from './ConversationContext.types'

import { ConversationContext } from './ConversationContext'
import { observeConversationLoadTime } from '../utils/observability'
import { useConversationCache, useTransactionCache } from '../../hooks/useMessagingCache'

type Props = {
  children: React.ReactNode
}

const ConversationProvider = ({ children }: Props) => {
  const currentConversationIdRef = useRef<string | number>(null)
  const currentTransactionIdRef = useRef<number>(null)
  const { getCachedConversation, setCachedConversation } = useConversationCache()
  const { getCachedTransaction, setCachedTransaction } = useTransactionCache()
  const [conversation, setConversation] = useState<Conversation>()
  const [conversationUi, setConversationUi] = useState<GetConversationUiState>({
    uiState: UiState.Idle,
    error: null,
    status: null,
  })

  const [transaction, setTransaction] = useState<TransactionModel | null>(null)
  const [transactionUi, setTransactionUi] = useState<GetTransactionUiState>({
    uiState: UiState.Idle,
    error: null,
  })

  const [conversationMessages, setConversationMessages] = useState<Array<ConversationMessage>>([])
  const [sendMessageUi, setSendMessageUi] = useState<SendMessageUiState>({ uiState: UiState.Idle })
  const [isFirstTimeListerEducationDialogVisible, setIsFirstTimeListerEducationDialogVisible] =
    useState(false)

  const loadStartTime = useRef<number>(0)
  const isLoadTimeObserved = useRef<boolean>(false)

  const isWebMessagingSvcFSEnabled = useFeatureSwitch('web_messaging_svc')

  const conversationError = conversationUi.error
  const conversationUiState = conversationUi.uiState || UiState.Idle
  const conversationRequestStatus = conversationUi.status

  useEffect(() => {
    if (isLoadTimeObserved.current) return

    const observeLoadEnd = (state: 'success' | 'failure') => {
      const conversationLoadTime = performance.now() - loadStartTime.current

      observeConversationLoadTime(state, conversationLoadTime)

      isLoadTimeObserved.current = true
    }

    if (conversationUiState === UiState.Success && !conversationError) {
      observeLoadEnd('success')
    } else if (conversationUiState === UiState.Failure) {
      observeLoadEnd('failure')
    }
  }, [conversationUiState, conversationError])

  const getTransactionRequest = useCallback(
    async (transactionId: number, conversationId: string | number) => {
      // If the transaction is already displayed, do not show a spinner
      if (currentTransactionIdRef.current !== transactionId) {
        setTransactionUi({ uiState: UiState.Pending, error: null })
      }

      const cachedTransaction = await getCachedTransaction(transactionId)
      if (cachedTransaction) {
        setTransaction(cachedTransaction)
        currentTransactionIdRef.current = transactionId
        setTransactionUi({ uiState: UiState.Success, error: null })
      }

      const response = await getTransaction({ id: transactionId })
      if (currentConversationIdRef.current !== conversationId) {
        return
      }

      if ('errors' in response) {
        if (!cachedTransaction) {
          setTransactionUi({ uiState: UiState.Failure, error: response.errors[0]?.value || null })
        }
      } else {
        const transformedTransaction = transformTransaction(response.transaction)
        setCachedTransaction(transformedTransaction)

        setTransactionUi({ uiState: UiState.Success, error: null })
        currentTransactionIdRef.current = transactionId
        setTransaction(transformedTransaction)
      }
    },
    [setCachedTransaction, getCachedTransaction],
  )

  const setConversationSuccess = useCallback(val => {
    setConversation(val)
    setConversationUi({ uiState: UiState.Success, error: null, status: HttpStatus.Ok })
    setConversationMessages(val.messages)
  }, [])

  const setTransactionSuccess = useCallback(val => {
    setTransaction(val)
    setTransactionUi({ uiState: UiState.Success, error: null })
  }, [])

  const fetchConversation = useCallback(
    async ({ conversationId, fromPostReply = false }: GetConversationRequestArgs) => {
      currentConversationIdRef.current = conversationId
      loadStartTime.current = performance.now()
      isLoadTimeObserved.current = false

      setConversationUi({
        uiState: UiState.Pending,
        status: null,
        error: null,
      })
      if (!fromPostReply) {
        setConversation(undefined)
        setConversationMessages([])
      }

      if (Number.isNaN(Number(conversationId))) {
        // TODO: backend returns 200 when passed a string. Should be fixed in backend
        setConversationUi({ uiState: UiState.Failure, error: null, status: HttpStatus.NotFound })
        setConversation(undefined)
        setConversationMessages([])

        return
      }

      const cachedConversation = await getCachedConversation(conversationId)
      if (cachedConversation) {
        setConversationUi({ uiState: UiState.Success, error: null, status: HttpStatus.Ok })
        if (cachedConversation.transactionId)
          getTransactionRequest(cachedConversation.transactionId, conversationId)
        else setTransaction(null)

        setConversation(cachedConversation)
        setConversationMessages(cachedConversation.messages)

        if (cachedConversation.isFirstTimeListerEducationRequired)
          setIsFirstTimeListerEducationDialogVisible(true)
      }

      const response = await getConversation(conversationId)
      if (currentConversationIdRef.current !== conversationId) {
        return
      }

      if ('errors' in response) {
        if (!cachedConversation && currentConversationIdRef.current === conversationId) {
          setConversationUi({
            uiState: UiState.Failure,
            error: response.errors[0]?.value || null,
            status: response.status,
          })
          setConversation(undefined)
          setConversationMessages([])
        }
      } else {
        const resolvedConversation = transformConversationResponse(response.conversation)
        setCachedConversation(resolvedConversation)
        setConversationUi({ uiState: UiState.Success, error: null, status: response.status })

        if (resolvedConversation.transactionId)
          getTransactionRequest(resolvedConversation.transactionId, conversationId)
        else setTransaction(null)

        setConversation(resolvedConversation)
        setConversationMessages(resolvedConversation.messages)

        if (resolvedConversation.isFirstTimeListerEducationRequired)
          setIsFirstTimeListerEducationDialogVisible(true)
      }
    },
    [
      setIsFirstTimeListerEducationDialogVisible,
      getTransactionRequest,
      getCachedConversation,
      setCachedConversation,
    ],
  )

  const fetchConversationNew = useCallback(
    async ({ conversationId, fromPostReply = false }: GetConversationRequestArgs) => {
      setConversationUi({
        uiState: UiState.Pending,
        status: null,
        error: null,
      })

      if (!fromPostReply) {
        setConversation(undefined)
        setConversationMessages([])
      }

      const response = await getConversationNew(conversationId)

      if ('errors' in response) {
        setConversationUi({
          uiState: UiState.Failure,
          error: response.errors[0]?.value || null,
          status: response.status,
        })
        setConversation(undefined)
        setConversationMessages([])

        return
      }

      const transformedConversation = transformConversationNewResponse(response)

      setConversationUi({ uiState: UiState.Success, error: null, status: response.status })
      setConversation(transformedConversation)
      setConversationMessages(transformedConversation.messages)
    },
    [],
  )

  const getConversationRequest = isWebMessagingSvcFSEnabled
    ? fetchConversationNew
    : fetchConversation

  const setIsFirstTimeListerEducationDialogVisibleCallback = useCallback(
    val => {
      setIsFirstTimeListerEducationDialogVisible(val)
    },
    [setIsFirstTimeListerEducationDialogVisible],
  )

  const clearConversation = useCallback(() => {
    setConversation(undefined)
    setConversationMessages([])

    setConversationUi({
      uiState: UiState.Idle,
      status: null,
      error: null,
    })
  }, [])

  const translateConversationRequest = useCallback(
    async ({ conversationId, translate }: TranslateConversationArgs) => {
      const response = await translateConversation({
        conversationId,
        translate,
      })

      if ('errors' in response) return

      setConversation(transformConversationResponse(response.conversation))

      setConversationUi({
        uiState: UiState.Success,
        status: response.status,
        error: null,
      })
    },
    [],
  )

  const [conversationLoadStartForEscrowOrderABTest, setConversationLoadStartForEscrowOrderABTest] =
    useState<number>(0)

  const contextValue = useMemo(
    () => ({
      conversation,
      conversationUiState,
      conversationRequestStatus,
      conversationError,
      getConversationRequest,
      setConversation: setConversationSuccess,
      setTransaction: setTransactionSuccess,
      clearConversation,
      translateConversationRequest,
      transaction,
      transactionUiState: transactionUi.uiState,
      conversationMessages,
      setConversationMessages,
      sendMessageUi,
      setSendMessageUi,
      isFirstTimeListerEducationDialogVisible,
      setIsFirstTimeListerEducationDialogVisible:
        setIsFirstTimeListerEducationDialogVisibleCallback,
      conversationLoadStartForEscrowOrderABTest: conversationLoadStartForEscrowOrderABTest ?? 0,
      setConversationLoadStartForEscrowOrderABTest,
    }),
    [
      conversation,
      conversationUiState,
      conversationRequestStatus,
      conversationError,
      getConversationRequest,
      setConversationSuccess,
      setTransactionSuccess,
      clearConversation,
      translateConversationRequest,
      transaction,
      transactionUi,
      conversationMessages,
      setConversationMessages,
      sendMessageUi,
      setSendMessageUi,
      isFirstTimeListerEducationDialogVisible,
      setIsFirstTimeListerEducationDialogVisibleCallback,
      conversationLoadStartForEscrowOrderABTest,
    ],
  )

  return (
    <ConversationContext.Provider value={contextValue}>{children}</ConversationContext.Provider>
  )
}

export default ConversationProvider
